import { supabase } from './supabaseClient';

const video_library_table = 'ml_video_library_staging';
const video_sessions_table = 'ml_video_watch_sessions_staging';

/**
 * Extract YouTube video ID from URL
 * @param {string} url - YouTube video URL
 * @returns {string|null} - Video ID or null
 */
export const extractYouTubeVideoId = (url) => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]+)/,
    /(?:youtu\.be\/)([a-zA-Z0-9_-]+)/,
    /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  return null;
};

/**
 * Generate YouTube thumbnail URL from video ID
 * @param {string} videoId - YouTube video ID
 * @returns {string} - Thumbnail URL
 */
export const getYouTubeThumbnail = (videoId) => {
  return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
};

/**
 * Fetch all videos for the current user
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - { data: Array of videos, error }
 */
export const fetchAllVideos = async (userId) => {
  const { data, error } = await supabase
    .from(video_library_table)
    .select('*, ml_courses_staging(id, title)')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  return { data, error };
};

/**
 * Fetch videos by course ID
 * @param {string} userId - User ID
 * @param {string} courseId - Course ID
 * @returns {Promise<Object>} - { data: Array of videos, error }
 */
export const fetchVideosByCourse = async (userId, courseId) => {
  const { data, error } = await supabase
    .from(video_library_table)
    .select('*')
    .eq('user_id', userId)
    .eq('course_id', courseId)
    .order('created_at', { ascending: false });

  return { data, error };
};

/**
 * Fetch AI recommended videos
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - { data: Array of videos, error }
 */
export const fetchAIRecommendedVideos = async (userId) => {
  const { data, error } = await supabase
    .from(video_library_table)
    .select('*, ml_courses_staging(id, title)')
    .eq('user_id', userId)
    .eq('is_ai_recommended', true)
    .order('created_at', { ascending: false });

  return { data, error };
};

/**
 * Add a new video to the library
 * @param {string} userId - User ID
 * @param {Object} videoData - Video information
 * @returns {Promise<Object>} - { data: Created video, error }
 */
export const addVideo = async (userId, videoData) => {
  const videoId = extractYouTubeVideoId(videoData.video_url);
  const thumbnailUrl = videoId ? getYouTubeThumbnail(videoId) : null;

  const payload = {
    user_id: userId,
    video_id: videoId,
    thumbnail_url: thumbnailUrl,
    ...videoData,
  };

  const { data, error } = await supabase
    .from(video_library_table)
    .insert(payload)
    .select()
    .single();

  return { data, error };
};

/**
 * Update video information
 * @param {string} videoId - Video ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} - { data: Updated video, error }
 */
export const updateVideo = async (videoId, updates) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { data: null, error: sessionError || new Error('No authenticated session.') };
  }

  const { data, error } = await supabase
    .from(video_library_table)
    .update(updates)
    .eq('id', videoId)
    .eq('user_id', session.user.id)
    .select()
    .single();

  return { data, error };
};

/**
 * Delete a video from the library
 * @param {string} videoId - Video ID
 * @returns {Promise<Object>} - { data, error }
 */
export const deleteVideo = async (videoId) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { data: null, error: sessionError || new Error('No authenticated session.') };
  }

  const { data, error } = await supabase
    .from(video_library_table)
    .delete()
    .eq('id', videoId)
    .eq('user_id', session.user.id);

  return { data, error };
};

/**
 * Update video watch progress
 * @param {string} videoId - Video ID
 * @param {number} progress - Progress in seconds
 * @param {boolean} isCompleted - Whether the video is completed
 * @returns {Promise<Object>} - { data, error }
 */
export const updateVideoProgress = async (videoId, progress, isCompleted = false) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { data: null, error: sessionError || new Error('No authenticated session.') };
  }

  const { data, error } = await supabase
    .from(video_library_table)
    .update({
      watch_progress: progress,
      is_completed: isCompleted,
    })
    .eq('id', videoId)
    .eq('user_id', session.user.id)
    .select()
    .single();

  return { data, error };
};

/**
 * Add AI recommended video
 * @param {string} userId - User ID
 * @param {Object} videoData - Video information including recommendation_reason
 * @returns {Promise<Object>} - { data: Created video, error }
 */
export const addAIRecommendedVideo = async (userId, videoData) => {
  const videoId = extractYouTubeVideoId(videoData.video_url);
  const thumbnailUrl = videoId ? getYouTubeThumbnail(videoId) : null;

  const payload = {
    user_id: userId,
    video_id: videoId,
    thumbnail_url: thumbnailUrl,
    is_ai_recommended: true,
    ...videoData,
  };

  const { data, error } = await supabase
    .from(video_library_table)
    .insert(payload)
    .select()
    .single();

  return { data, error };
};

/**
 * Search videos by title or tags
 * @param {string} userId - User ID
 * @param {string} searchTerm - Search term
 * @returns {Promise<Object>} - { data: Array of videos, error }
 */
export const searchVideos = async (userId, searchTerm) => {
  const { data, error } = await supabase
    .from(video_library_table)
    .select('*, ml_courses_staging(id, title)')
    .eq('user_id', userId)
    .or(`title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`)
    .order('created_at', { ascending: false });

  return { data, error };
};

/**
 * Fetch video with full details for watching
 * @param {string} videoId - Video ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - { data: Video with course details, error }
 */
export const fetchVideoForWatching = async (videoId, userId) => {
  const { data, error } = await supabase
    .from(video_library_table)
    .select(
      `
      *,
      ml_courses_staging (
        *
      )
    `
    )
    .eq('id', videoId)
    .eq('user_id', userId)
    .single();

  return { data, error };
};

/**
 * Create watch session
 * @param {string} userId - User ID
 * @param {string} videoId - Video ID
 * @param {string} courseId - Course ID
 * @returns {Promise<Object>} - { data: Created session, error }
 */
export const createWatchSession = async (userId, videoId, courseId) => {
  const { data, error } = await supabase
    .from(video_sessions_table)
    .insert({
      user_id: userId,
      video_id: videoId,
      course_id: courseId,
      started_at: new Date().toISOString(),
    })
    .select()
    .single();

  return { data, error };
};

/**
 * Update watch session
 * @param {string} sessionId - Session ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} - { data: Updated session, error }
 */
export const updateWatchSession = async (sessionId, updates) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { data: null, error: sessionError || new Error('No authenticated session.') };
  }

  const { data, error } = await supabase
    .from(video_sessions_table)
    .update(updates)
    .eq('id', sessionId)
    .eq('user_id', session.user.id)
    .select()
    .single();

  return { data, error };
};

/**
 * Save Librarian interaction to video
 * @param {string} videoId - Video ID
 * @param {Object} interaction - Interaction data
 * @returns {Promise<Object>} - { data, error }
 */
export const saveLibrarianInteraction = async (videoId, interaction) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { data: null, error: sessionError || new Error('No authenticated session.') };
  }

  const { data: video } = await supabase
    .from(video_library_table)
    .select('librarian_interactions')
    .eq('id', videoId)
    .eq('user_id', session.user.id)
    .single();

  const interactions = video?.librarian_interactions || [];
  interactions.push({
    timestamp: new Date().toISOString(),
    ...interaction,
  });

  const { data, error } = await supabase
    .from(video_library_table)
    .update({ librarian_interactions: interactions })
    .eq('id', videoId)
    .eq('user_id', session.user.id)
    .select()
    .single();

  return { data, error };
};

/**
 * Update video's current timestamp for resume
 * @param {string} videoId - Video ID
 * @param {number} currentTime - Current timestamp in seconds
 * @returns {Promise<Object>} - { data, error }
 */
export const updateVideoTimestamp = async (videoId, currentTime) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { data: null, error: sessionError || new Error('No authenticated session.') };
  }

  const { data, error } = await supabase
    .from(video_library_table)
    .update({
      playback_position_seconds: currentTime,
      last_watched_at: new Date().toISOString(),
    })
    .eq('id', videoId)
    .eq('user_id', session.user.id)
    .select()
    .single();

  return { data, error };
};

/**
 * Fork videos from one course to another for a new user
 * @param {string} originalCourseId - Original course ID
 * @param {string} newCourseId - New forked course ID
 * @param {string} newUserId - New user ID who is forking the course
 * @param {string} originalUserId - Original course owner's user ID (optional, will fetch if not provided)
 * @returns {Promise<Object>} - { data: Array of forked videos, error }
 */
export const forkVideosForCourse = async (
  originalCourseId,
  newCourseId,
  newUserId,
  originalUserId = null
) => {
  // If originalUserId is not provided, fetch it from the course
  let ownerUserId = originalUserId;
  if (!ownerUserId) {
    const { data: courseData, error: courseError } = await supabase
      .from('ml_courses_staging')
      .select('owner_id')
      .eq('id', originalCourseId)
      .single();

    if (courseError || !courseData) {
      return { data: [], error: courseError || new Error('Could not find course owner') };
    }
    ownerUserId = courseData.owner_id;
  }

  // Fetch all videos linked to the original course AND owned by the original user

  const { data: originalVideos, error: fetchError } = await supabase
    .from(video_library_table)
    .select('*')
    .eq('course_id', originalCourseId)
    .eq('user_id', ownerUserId);

  if (fetchError) {
    return { data: [], error: fetchError };
  }

  if (!originalVideos || originalVideos.length === 0) {
    return { data: [], error: null };
  }

  // Prepare forked videos for the new user and new course
  const forkedVideos = originalVideos.map((video) => ({
    user_id: newUserId,
    course_id: newCourseId,
    video_id: video.video_id,
    video_url: video.video_url,
    title: video.title,
    description: video.description,
    thumbnail_url: video.thumbnail_url,
    is_ai_recommended: video.is_ai_recommended || false,
    recommendation_reason: video.recommendation_reason,
    tags: video.tags,
    // Reset progress for the new user
    watch_progress: 0,
    playback_position_seconds: 0,
    is_completed: false,
    // Don't copy librarian interactions - let the new user have fresh interactions
    librarian_interactions: null,
  }));

  // Insert forked videos
  const { data: insertedVideos, error: insertError } = await supabase
    .from(video_library_table)
    .insert(forkedVideos)
    .select();

  if (insertError) {
    return { data: [], error: insertError };
  }

  return { data: insertedVideos, error: null };
};
